import React, { Fragment, forwardRef } from 'react';
import { styled, ButtonBase } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { useViewport } from 'contexts/viewport';
import { Accordion, AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Box from 'shell/components/Box';
import Divider from 'shell/components/Divider';
import FormLabel from 'shell/components/FormLabel';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import MenuItem from 'shell/components/MenuItem';
import Radio from 'shell/components/Radio';
import Select from 'shell/components/Select';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextArea from 'shell/components/TextArea';
import TextInput from 'shell/components/TextInput';
import Tooltip from 'shell/components/Tooltip';
import { numberRegex } from 'utils/constants';

const TYPES = ['create', 'submit'];
const MAX_LENGTH = 200;

const RootStack = styled(Stack)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '12px',
    };
});

const PrimaryHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomSelect = styled(Select)(({ theme }) => {
    return {
        width: 120,
        '& > .MuiSelect-select': {
            padding: theme.spacing(1.5, 3),
        },
    };
});

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(2),
        background: theme.palette.ensightful.neutral[100],
        '&.Mui-expanded': {
            borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '0.75rem',
    };
});

const CustomAccordionDetails = styled(AccordionDetails)(() => {
    return {
        margin: 0,
    };
});

const OverflowStack = styled(Stack)(() => {
    return {
        overflowX: 'auto',
    };
});

const OptionStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(3, 5),
        ...(device === 'mobile'
            ? {
                  width: '100%',
              }
            : {
                  minWidth: 240,
                  maxWidth: 240,
              }),
    };
});

const CustomTextArea = styled(TextArea)(() => {
    return {
        '& > p.MuiFormHelperText-root': {
            textAlign: 'right',
        },
    };
});

const CustomDivider = styled(Divider)(({ device, isLast }) => {
    return {
        ...(device === 'mobile' &&
            isLast && {
                marginBottom: 14,
            }),
    };
});

const FooterBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '0 0 12px 12px',
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

const NeutralHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(5, 3),
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(3),
        background: theme.palette.ensightful.primary[50],
    };
});

const CriterionHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(2.5),
        background: theme.palette.ensightful.neutral[100],
    };
});

const CustomFormLabel = styled(FormLabel)(() => {
    return {
        alignSelf: 'center',
        width: 'fit-content',
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        flex: 1,
        minWidth: 240,
        padding: theme.spacing(3),
    };
});

const CustomRadio = styled(Radio)(({ theme }) => {
    return {
        alignSelf: 'flex-start',
        margin: theme.spacing(-2),
    };
});

const CreateRubricTable = forwardRef(
    (
        {
            data,
            disabled,
            errors,
            sort,
            setSort,
            onChange,
            onClickAddCriterion,
            onClickEditCriterion,
            onClickAddOption,
            onClickDeleteOption,
            onDragEnd,
            ...rest
        },
        ref
    ) => {
        const { device } = useViewport();
        const totalMaxPoints = data
            .map((criterion) => {
                return Math.max(...criterion.options.map((option) => Number(option.rate)));
            })
            .reduce((acc, cur) => acc + cur, 0);

        return (
            <RootStack ref={ref} {...rest}>
                <PrimaryHeaderStack direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            Criteria
                        </Text>
                        <IconButton
                            aria-label={'add a new criterion'}
                            disabled={disabled}
                            kind={'add'}
                            size={'small'}
                            variant={'circle'}
                            onClick={onClickAddCriterion}
                        />
                    </Stack>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text noWrap variant={'p3'}>
                            Sort Ratings
                        </Text>
                        <Tooltip
                            content={
                                <Text color={'inherit'} variant={'p2'}>
                                    The ratings will automatically be sorted in the version that is shown to students
                                    even if it is not in order while you are editing. You can decide if you want it
                                    displayed in ascending or descending order.
                                </Text>
                            }
                            type={'info'}
                        />
                        <CustomSelect disabled={disabled} value={sort} onChange={(e) => setSort(e.target.value)}>
                            <MenuItem value={'desc'}>Descending</MenuItem>
                            <MenuItem value={'asc'}>Ascending</MenuItem>
                        </CustomSelect>
                    </Stack>
                </PrimaryHeaderStack>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId={'criteria-droppable'}>
                        {(provided) => (
                            <Stack ref={provided.innerRef} {...provided.droppableProps}>
                                {data.map((criterion, index) => {
                                    const criterionMaxPoints = Math.max(
                                        ...criterion.options.map((option) => Number(option.rate))
                                    );
                                    return (
                                        <Draggable key={criterion.id} draggableId={criterion.id} index={index}>
                                            {(provided) => (
                                                <Accordion
                                                    ref={provided.innerRef}
                                                    defaultExpanded
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    aria-roledescription={undefined}
                                                >
                                                    <CustomAccordionSummary role={'region'}>
                                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                                            <CustomIcon kind={'reorder'} />
                                                            <Stack spacing={3}>
                                                                <Stack
                                                                    alignItems={'center'}
                                                                    direction={'row'}
                                                                    spacing={2}
                                                                >
                                                                    <Text variant={'p2'}>{criterion.title}</Text>
                                                                    <IconButton
                                                                        aria-label={'edit criterion'}
                                                                        disabled={disabled}
                                                                        kind={'edit'}
                                                                        size={'small'}
                                                                        variant={'square'}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onClickEditCriterion(criterion.id);
                                                                        }}
                                                                    />
                                                                </Stack>
                                                                <Stack spacing={2}>
                                                                    <Text variant={'p2'}>
                                                                        Pts:{' '}
                                                                        {criterionMaxPoints
                                                                            ? `/${criterionMaxPoints}`
                                                                            : '-'}
                                                                    </Text>
                                                                    <Text variant={'p4'}>{criterion.description}</Text>
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </CustomAccordionSummary>
                                                    <CustomAccordionDetails>
                                                        <OverflowStack
                                                            direction={device === 'mobile' ? 'column' : 'row'}
                                                        >
                                                            {criterion.options.map((option, index) => (
                                                                <Fragment key={option.id}>
                                                                    <OptionStack
                                                                        id={option.id}
                                                                        alignItems={'flex-end'}
                                                                        device={device}
                                                                        spacing={2}
                                                                    >
                                                                        <TextInput
                                                                            disabled={disabled}
                                                                            error={Boolean(
                                                                                errors?.[criterion.id]?.[option.id]
                                                                                    ?.rate
                                                                            )}
                                                                            helperText={
                                                                                errors?.[criterion.id]?.[option.id]
                                                                                    ?.rate
                                                                            }
                                                                            label={'Points'}
                                                                            name={'rate'}
                                                                            required
                                                                            size={'small'}
                                                                            value={option.rate}
                                                                            onBlur={() => setSort(sort)}
                                                                            onChange={(e) => {
                                                                                if (
                                                                                    !e.target.value ||
                                                                                    numberRegex.test(e.target.value)
                                                                                ) {
                                                                                    onChange(
                                                                                        criterion.id,
                                                                                        option.id,
                                                                                        e.target.name,
                                                                                        e.target.value
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                        <TextInput
                                                                            disabled={disabled}
                                                                            label={'Title'}
                                                                            name={'title'}
                                                                            size={'small'}
                                                                            value={option.title}
                                                                            onChange={(e) =>
                                                                                onChange(
                                                                                    criterion.id,
                                                                                    option.id,
                                                                                    e.target.name,
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                        <CustomTextArea
                                                                            disabled={disabled}
                                                                            helperText={
                                                                                option.description
                                                                                    ? `${option.description.length}/${MAX_LENGTH}`
                                                                                    : `/${MAX_LENGTH}`
                                                                            }
                                                                            inputProps={{ maxLength: MAX_LENGTH }}
                                                                            label={'Description'}
                                                                            name={'description'}
                                                                            rows={2}
                                                                            size={'small'}
                                                                            value={option.description}
                                                                            onChange={(e) =>
                                                                                onChange(
                                                                                    criterion.id,
                                                                                    option.id,
                                                                                    e.target.name,
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                        <IconButton
                                                                            aria-label={'delete option'}
                                                                            disabled={
                                                                                disabled ||
                                                                                criterion.options.length <= 2
                                                                            }
                                                                            kind={'delete'}
                                                                            size={'small'}
                                                                            variant={'square'}
                                                                            onClick={() =>
                                                                                onClickDeleteOption(
                                                                                    criterion.id,
                                                                                    option.id
                                                                                )
                                                                            }
                                                                        />
                                                                    </OptionStack>
                                                                    <CustomDivider
                                                                        device={device}
                                                                        flexItem
                                                                        isLast={index === criterion.options.length - 1}
                                                                        orientation={
                                                                            device === 'mobile'
                                                                                ? 'horizontal'
                                                                                : 'vertical'
                                                                        }
                                                                        role={'presentation'}
                                                                        tabIndex={0}
                                                                    >
                                                                        <IconButton
                                                                            aria-label={'add a new option'}
                                                                            disabled={disabled}
                                                                            kind={'add'}
                                                                            size={'small'}
                                                                            variant={'circle'}
                                                                            onClick={() =>
                                                                                onClickAddOption(
                                                                                    criterion.id,
                                                                                    index + 1
                                                                                )
                                                                            }
                                                                        />
                                                                    </CustomDivider>
                                                                </Fragment>
                                                            ))}
                                                        </OverflowStack>
                                                    </CustomAccordionDetails>
                                                </Accordion>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </Stack>
                        )}
                    </Droppable>
                </DragDropContext>
                <FooterBox>
                    <Stack direction={'row'} divider={<Divider flexItem orientation={'vertical'} />}>
                        <CustomText sx={{ flex: 1 }} align={'right'} variant={'p2'}>
                            Total Points
                        </CustomText>
                        <CustomText variant={'p2'}>{totalMaxPoints || '-'}</CustomText>
                    </Stack>
                </FooterBox>
            </RootStack>
        );
    }
);

const SubmitRubricTable = forwardRef(
    ({ data, disabled, errors, form, name, number, total, onChange, ...rest }, ref) => {
        const { device } = useViewport();

        const totalMaxPoints = data
            .map((criterion) => {
                return Math.max(...criterion.options.map((option) => Number(option.rate)));
            })
            .reduce((acc, cur) => acc + cur, 0);

        const totalSelectedPoints = data.reduce((acc, cur) => {
            return (
                acc +
                cur.options.reduce((acc, cur) => {
                    if (Object.values(form).includes(cur.id)) return acc + Number(cur.rate || 0);
                    return acc;
                }, 0)
            );
        }, 0);

        return (
            <RootStack ref={ref} {...rest}>
                <NeutralHeaderStack spacing={1}>
                    <Text variant={'sh1'} weight={'semibold'}>
                        Please Evaluate:{' '}
                        <Text color={'primary'} component={'span'} variant={'sh1'}>
                            {name}
                        </Text>
                    </Text>
                    <Text variant={'p2'}>
                        ({number}/{total} Evaluations)
                    </Text>
                </NeutralHeaderStack>
                <CustomBox>
                    <Text variant={'p1'} weight={'semibold'}>
                        Criteria
                    </Text>
                </CustomBox>
                <Stack>
                    {data.map((criterion) => {
                        const selectedOption = criterion.options.find(({ id }) => id === form?.[criterion.id]);
                        const criterionMaxPoints = Math.max(...criterion.options.map((option) => Number(option.rate)));
                        return (
                            <Stack key={criterion.id}>
                                <CriterionHeaderStack spacing={2.5}>
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Text variant={'p2'}>{criterion.title}</Text>
                                        <Text variant={'p2'}>
                                            Pts: {selectedOption?.rate || '-'}/{criterionMaxPoints}
                                        </Text>
                                    </Stack>
                                    <Text variant={'p4'}>{criterion.description}</Text>
                                </CriterionHeaderStack>
                                <OverflowStack
                                    direction={device === 'mobile' ? 'column' : 'row'}
                                    divider={
                                        <Divider
                                            flexItem
                                            orientation={device === 'mobile' ? 'horizontal' : 'vertical'}
                                        />
                                    }
                                >
                                    {criterion.options.map((option) => (
                                        <CustomButtonBase
                                            key={option.id}
                                            component={'div'}
                                            disabled={disabled}
                                            role={'presentation'}
                                            tabIndex={0}
                                            onClick={() => onChange(criterion.id, option.id)}
                                        >
                                            <Stack spacing={3} sx={{ width: '100%', height: '100%' }}>
                                                <Stack direction={'row'} justifyContent={'space-between'}>
                                                    <Stack alignItems={'flex-start'} spacing={3}>
                                                        <CustomFormLabel htmlFor={`${option.id}-option`}>
                                                            <Text
                                                                data-cy={`rubric-points-${option.rate}`}
                                                                variant={'p2'}
                                                            >
                                                                {option.rate} pts
                                                            </Text>
                                                        </CustomFormLabel>
                                                        <Text variant={'p2'}>{option.title}</Text>
                                                    </Stack>
                                                    <CustomRadio
                                                        inputProps={{
                                                            id: `${option.id}-option`,
                                                        }}
                                                        checked={form?.[criterion.id] === option.id}
                                                        disabled={disabled}
                                                        error={errors?.[criterion.id]}
                                                    />
                                                </Stack>
                                                <Text align={'left'} variant={'p4'}>
                                                    {option.description}
                                                </Text>
                                            </Stack>
                                        </CustomButtonBase>
                                    ))}
                                </OverflowStack>
                            </Stack>
                        );
                    })}
                </Stack>
                <FooterBox>
                    <Stack direction={'row'} divider={<Divider flexItem orientation={'vertical'} />}>
                        <CustomText sx={{ flex: 1 }} align={'right'} variant={'p2'}>
                            Total Points Out of {totalMaxPoints}
                        </CustomText>
                        <CustomText variant={'p2'}>{totalSelectedPoints}</CustomText>
                    </Stack>
                </FooterBox>
            </RootStack>
        );
    }
);

export default forwardRef(({ type, ...rest }, ref) => {
    if (!TYPES.includes(type)) throw new Error(`Shell: Invalid type "${type}" in component RubricTable.`);

    if (type === 'create') return <CreateRubricTable ref={ref} {...rest} />;
    if (type === 'submit') return <SubmitRubricTable ref={ref} {...rest} />;
    return <></>;
});
